import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export default function GradesTab({ levelCode }) {
  const [grades, setGrades] = useState([]);
  const [error, setError] = useState(null);
  const [editingGrade, setEditingGrade] = useState(null);
  const [showAddGradeModal, setShowAddGradeModal] = useState(false);
  const [viewMode, setViewMode] = useState('assignment'); // 'assignment', 'student', or 'grid'
  const [newGrade, setNewGrade] = useState({
    student_id: '',
    assignment_id: '',
    score: '',
    feedback: ''
  });
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [drawerType, setDrawerType] = useState(null); // 'student' or 'assignment'
  const [drawerData, setDrawerData] = useState(null);
  const [drawerError, setDrawerError] = useState(null);
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());
  const [expandedStudents, setExpandedStudents] = useState(new Set());

  useEffect(() => {
    if (levelCode) {
      fetchGrades();
      fetchStudents();
      fetchAssignments();
    }
  }, [levelCode]);

  const fetchGrades = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('grades')
        .select(`
          *,
          assignment:assignments!inner(*),
          student:users(name, code)
        `)
        .eq('assignment.level_code', levelCode)
        .order('graded_at', { ascending: false });

      if (error) throw error;
      setGrades(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('student_enrollment')
        .select('student:users(id, name, code)')
        .eq('level_code', levelCode);

      if (error) throw error;
      // Sort students alphabetically by name
      const sortedStudents = (data?.map(item => item.student) || []).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setStudents(sortedStudents);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('assignments')
        .select('*')
        .eq('level_code', levelCode)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssignments(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGradeUpdate = async (gradeId, newScore, newFeedback) => {
    try {
      const { error } = await supabase
        .from('grades')
        .update({
          score: parseFloat(newScore),
          feedback: newFeedback,
          graded_at: new Date().toISOString()
        })
        .eq('id', gradeId);

      if (error) throw error;
      await fetchGrades();
      setEditingGrade(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddGrade = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('grades')
        .insert([{
          ...newGrade,
          score: parseFloat(newGrade.score),
          graded_at: new Date().toISOString()
        }]);

      if (error) throw error;
      await fetchGrades();
      setShowAddGradeModal(false);
      setNewGrade({
        student_id: '',
        assignment_id: '',
        score: '',
        feedback: ''
      });
    } catch (err) {
      setError(err.message);
    }
  };

  // New function to organize grades by assignment
  const getGradesByAssignment = () => {
    const gradesByAssignment = {};
    assignments.forEach(assignment => {
      const assignmentGrades = grades.filter(grade => grade.assignment_id === assignment.id);
      // Sort grades by student name alphabetically
      assignmentGrades.sort((a, b) => {
        const nameA = a.student?.name || '';
        const nameB = b.student?.name || '';
        return nameA.localeCompare(nameB);
      });
      gradesByAssignment[assignment.id] = {
        assignment,
        grades: assignmentGrades
      };
    });
    return gradesByAssignment;
  };

  // New function to organize grades by student
  const getGradesByStudent = () => {
    const gradesByStudent = {};
    students.forEach(student => {
      gradesByStudent[student.id] = {
        student,
        grades: grades.filter(grade => grade.student_id === student.id)
      };
    });
    return gradesByStudent;
  };

  // New function to calculate final grade for a student
  const calculateFinalGrade = (studentGrades) => {
    if (!studentGrades || studentGrades.length === 0) return { totalScore: 0, maxPossible: 0, percentage: 0 };
    const counted = studentGrades.filter(g => !g.excused);
    const totalScore = counted.reduce((sum, grade) => sum + (grade.score || 0), 0);
    const maxPossible = counted.reduce((sum, grade) => sum + (grade.assignment?.max_score ?? 0), 0);
    const percentage = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
    return { totalScore, maxPossible, percentage };
  };

  // Get grade for a specific student-assignment combination
  const getGradeForStudentAssignment = (studentId, assignmentId) => {
    return grades.find(grade => 
      grade.student_id === studentId && grade.assignment_id === assignmentId
    );
  };

  // Handle grade update in grid
  const handleGridGradeUpdate = async (studentId, assignmentId, newScore) => {
    try {
      const existingGrade = getGradeForStudentAssignment(studentId, assignmentId);
      
      if (existingGrade) {
        // Update existing grade
        const { error } = await supabase
          .from('grades')
          .update({
            score: parseFloat(newScore),
            graded_at: new Date().toISOString()
          })
          .eq('id', existingGrade.id);

        if (error) throw error;
      } else {
        // Create new grade
        const { error } = await supabase
          .from('grades')
          .insert([{
            student_id: studentId,
            assignment_id: assignmentId,
            score: parseFloat(newScore),
            graded_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }
      
      await fetchGrades();
    } catch (err) {
      setError(err.message);
    }
  };

  // Open drawer for student or assignment
  const openDrawer = (type, data) => {
    setDrawerType(type);
    setDrawerData(data);
    setDrawerError(null);
    setDrawerOpen(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerType(null);
    setDrawerData(null);
    setDrawerError(null);
    setSelectedGrade(null);
  };

  // Toggle assignment expansion
  const toggleAssignment = (assignmentId) => {
    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  // Toggle student expansion
  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Handle grade update from drawer (excused = true means mark as excused, no score)
  const handleDrawerGradeUpdate = async (studentId, assignmentId, score, feedback, excused = false) => {
    setDrawerError(null);
    try {
      const existingGrade = getGradeForStudentAssignment(studentId, assignmentId);
      const payload = {
        score: excused ? null : (score != null && score !== '' ? parseFloat(score) : null),
        feedback: feedback || null,
        graded_at: new Date().toISOString(),
        excused: !!excused
      };

      if (existingGrade) {
        const { error } = await supabase
          .from('grades')
          .update(payload)
          .eq('id', existingGrade.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('grades')
          .insert([{
            student_id: studentId,
            assignment_id: assignmentId,
            ...payload
          }]);

        if (error) throw error;
      }

      await fetchGrades();
      closeDrawer();
    } catch (err) {
      const msg = err?.message || 'Something went wrong';
      setError(msg);
      setDrawerError(msg);
      throw err;
    }
  };


  // New function to calculate letter grade
  const calculateLetterGrade = (percentage) => {
    if (percentage >= 93) return 'A';
    if (percentage >= 90) return 'A-';
    if (percentage >= 87) return 'B+';
    if (percentage >= 83) return 'B';
    if (percentage >= 80) return 'B-';
    if (percentage >= 77) return 'C+';
    if (percentage >= 73) return 'C';
    if (percentage >= 70) return 'C-';
    if (percentage >= 67) return 'D+';
    if (percentage >= 63) return 'D';
    if (percentage >= 60) return 'D-';
    return 'F';
  };

  // New function to calculate letter grade for individual assignment
  const calculateAssignmentLetterGrade = (score, maxScore) => {
    if (!score || !maxScore) return '-';
    const percentage = (score / maxScore) * 100;
    return calculateLetterGrade(percentage);
  };

  // Render grid view with students as rows and assignments as columns
  const renderGridView = () => {
    if (assignments.length === 0) {
      return (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-neutral-900 mb-2">No assignments yet</h3>
          <p className="text-neutral-500">Create assignments to start grading students.</p>
        </div>
      );
    }

    return (
      <div className="card p-0 overflow-hidden">
        <div className="p-4 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Grade Grid</h3>
              <p className="text-sm text-neutral-600">Click any cell to edit grades</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-neutral-600">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                <span>90%+</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
                <span>70-89%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                <span>&lt;70%</span>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-white">
                <th className="text-left py-4 px-4 font-semibold text-neutral-700 sticky left-0 bg-white z-20 shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    Student
                  </div>
                </th>
                {assignments.map((assignment) => (
                  <th key={assignment.id} className="text-center py-4 px-3 font-semibold text-neutral-700 min-w-[140px] bg-neutral-50">
                    <div className="flex flex-col items-center space-y-1">
                      <div className="text-xs text-neutral-500 font-medium">
                        {new Date(assignment.due_date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="text-sm font-semibold text-neutral-900 truncate max-w-[120px]" title={assignment.title}>
                        {assignment.title}
                      </div>
                      <div className="text-xs text-neutral-400 bg-neutral-200 px-2 py-1 rounded-full">
                        /{assignment.max_score}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => {
                const studentGrades = grades.filter(grade => grade.student_id === student.id);
                const { totalScore, maxPossible, percentage } = calculateFinalGrade(studentGrades);
                
                return (
                  <tr key={student.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors duration-150">
                    <td className="py-4 px-4 sticky left-0 bg-white z-10 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-sm">
                          <span className="text-white font-bold text-sm">
                            {student.name?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-neutral-900 truncate">{student.name}{student.code ? <span className="font-normal text-neutral-500"> · {student.code}</span> : ''}</div>
                          <div className="text-xs text-neutral-500">
                            {totalScore}/{maxPossible} points
                          </div>
                          <div className={`text-xs font-medium ${
                            percentage >= 90 ? 'text-green-600' :
                            percentage >= 70 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {percentage}% average
                          </div>
                        </div>
                      </div>
                    </td>
                    {assignments.map((assignment) => {
                      const grade = getGradeForStudentAssignment(student.id, assignment.id);
                      const isExcused = grade?.excused;
                      const gradePercentage = grade && !isExcused && grade.score !== null && grade.score !== undefined ? Math.round((grade.score / assignment.max_score) * 100) : 0;
                      const gradeColor = isExcused ? 'bg-neutral-100 border-neutral-300 text-neutral-600' :
                                        gradePercentage >= 90 ? 'bg-green-50 border-green-200 text-green-800' :
                                        gradePercentage >= 70 ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                                        gradePercentage > 0 ? 'bg-red-50 border-red-200 text-red-800' :
                                        'bg-neutral-50 border-neutral-200 text-neutral-500';
                      
                      return (
                        <td key={assignment.id} className="py-3 px-2 text-center">
                          <div 
                            className={`cursor-pointer hover:scale-105 transition-all duration-150 rounded-lg border-2 px-3 py-2 min-h-[50px] flex flex-col items-center justify-center ${gradeColor} hover:shadow-sm`}
                            onClick={() => {
                              const cellGrade = grade || {
                                student_id: student.id,
                                assignment_id: assignment.id,
                                score: null,
                                excused: false,
                                student: student,
                                assignment: assignment
                              };
                              setSelectedGrade(cellGrade);
                              openDrawer('assignment', { assignment, grade: cellGrade });
                            }}
                            title={`Click to edit grade for ${student.name} - ${assignment.title}`}
                          >
                            {isExcused ? (
                              <div className="text-center">
                                <div className="text-sm font-semibold opacity-90">Excused</div>
                              </div>
                            ) : grade && grade.score !== null && grade.score !== undefined ? (
                              <div className="text-center">
                                <div className="font-bold text-lg">{grade.score}</div>
                                <div className="text-xs font-medium opacity-75">
                                  {gradePercentage}%
                                </div>
                                <div className="text-xs opacity-60">
                                  /{assignment.max_score}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <div className="text-lg font-medium opacity-50">-</div>
                                <div className="text-xs opacity-40">Click to add</div>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Footer with Statistics */}
        <div className="p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-neutral-600">Students:</span>
                <span className="font-semibold text-neutral-900">{students.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-neutral-600">Assignments:</span>
                <span className="font-semibold text-neutral-900">{assignments.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-neutral-600">Grades Entered:</span>
                <span className="font-semibold text-neutral-900">{grades.length}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-neutral-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Click any cell to edit grades</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAssignmentView = () => {
    const gradesByAssignment = getGradesByAssignment();
    
    return (
      <div className="space-y-2">
        {assignments.map((assignment, index) => {
          const assignmentGrades = gradesByAssignment[assignment.id]?.grades || [];
          const isExpanded = expandedAssignments.has(assignment.id);
          const studentsWithoutGrades = students.filter(student => 
            !assignmentGrades.some(g => g.student_id === student.id)
          );
          
          return (
            <div key={assignment.id} className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
              {/* Clickable Header */}
              <div 
                className="flex justify-between items-center px-4 py-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => toggleAssignment(assignment.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <svg 
                      className={`w-4 h-4 text-neutral-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-900">{assignment.title}</h3>
                      <p className="text-xs text-neutral-500">/{assignment.max_score}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-primary-600">{assignmentGrades.length}/{students.length}</div>
                </div>
              </div>
              
              {/* Collapsible Content */}
              {isExpanded && (
                <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50/50">
                  {assignmentGrades.length === 0 && studentsWithoutGrades.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-xs text-neutral-500">No grades recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {assignmentGrades.map(grade => (
                        <div 
                          key={grade.id} 
                          className="flex items-center justify-between px-3 py-2 bg-white rounded border border-neutral-200 cursor-pointer hover:bg-neutral-50 hover:border-primary-300 transition-colors"
                          onClick={() => {
                            setSelectedGrade(grade);
                            openDrawer('assignment', { assignment, grade });
                          }}
                        >
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-neutral-900">{grade.student?.name}{grade.student?.code ? <span className="text-neutral-500"> · {grade.student.code}</span> : ''}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {grade.excused ? (
                              <span className="text-sm font-medium text-neutral-500">Excused</span>
                            ) : (
                              <>
                                <span className="text-sm font-semibold text-neutral-900">
                                  {grade.score !== null && grade.score !== undefined ? grade.score : '-'}
                                </span>
                                <span className="text-xs text-neutral-500">/{grade.assignment?.max_score || assignment.max_score}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                  
                      {/* Show students without grades */}
                      {students.filter(student => 
                        !assignmentGrades.some(g => g.student_id === student.id)
                      ).sort((a, b) => a.name.localeCompare(b.name)).map(student => (
                        <div 
                          key={`missing-${student.id}`}
                          className="flex items-center justify-between px-3 py-2 bg-warning-50 rounded border border-warning-200 cursor-pointer hover:bg-warning-100 transition-colors"
                          onClick={() => {
                            const missingGrade = {
                              student_id: student.id,
                              assignment_id: assignment.id,
                              score: null,
                              student: student,
                              assignment: assignment
                            };
                            setSelectedGrade(missingGrade);
                            openDrawer('assignment', { assignment, grade: missingGrade });
                          }}
                        >
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-neutral-900">{student.name}{student.code ? <span className="text-neutral-500"> · {student.code}</span> : ''}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-neutral-400">-</span>
                            <span className="text-xs text-neutral-500">/{assignment.max_score}</span>
                          </div>
                        </div>
                      ))}
                </div>
              )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStudentView = () => {
    const gradesByStudent = getGradesByStudent();
    
    return (
      <div className="space-y-2">
        {students.map((student, index) => {
          const studentGrades = gradesByStudent[student.id]?.grades || [];
          const { totalScore, maxPossible, percentage } = calculateFinalGrade(studentGrades);
          
          // Find assignments that don't have grades for this student
          const missingAssignments = assignments.filter(assignment => 
            !studentGrades.some(grade => grade.assignment_id === assignment.id)
          );
          const isExpanded = expandedStudents.has(student.id);
          
          return (
            <div key={student.id} className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
              {/* Clickable Header */}
              <div 
                className="flex justify-between items-center px-4 py-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => toggleStudent(student.id)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <svg 
                    className={`w-4 h-4 text-neutral-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">{student.name}{student.code ? <span className="font-normal text-neutral-500"> · {student.code}</span> : ''}</h3>
                    <p className="text-xs text-neutral-500">{studentGrades.length}/{assignments.length} graded</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm font-bold text-primary-600">{percentage}%</div>
                  <div className="text-xs text-neutral-500">{totalScore}/{maxPossible}</div>
                </div>
              </div>
              
              {/* Collapsible Content */}
              {isExpanded && (
                <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50/50">
              
                  {/* Missing Assignments Alert */}
                  {missingAssignments.length > 0 && (
                    <div className="mb-2 px-2 py-1.5 bg-warning-50 border border-warning-200 rounded text-xs text-warning-800">
                      {missingAssignments.length} missing grade{missingAssignments.length > 1 ? 's' : ''}
                    </div>
                  )}
                  
                  {/* Final Grade Summary */}
                  <div className="mb-2 px-2 py-1.5 bg-primary-50 rounded border border-primary-100">
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <div className="font-semibold text-primary-600">{totalScore}</div>
                        <div className="text-neutral-500">Points</div>
                      </div>
                      <div>
                        <div className="font-semibold text-primary-600">{maxPossible}</div>
                        <div className="text-neutral-500">Max</div>
                      </div>
                      <div>
                        <div className="font-semibold text-primary-600">{percentage}%</div>
                        <div className="text-neutral-500">Avg</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Grades List */}
                  {studentGrades.length === 0 && missingAssignments.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-xs text-neutral-500">No grades recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {studentGrades.map(grade => (
                        <div 
                          key={grade.id} 
                          className="flex items-center justify-between px-3 py-2 bg-white rounded border border-neutral-200 cursor-pointer hover:bg-neutral-50 hover:border-primary-300 transition-colors"
                          onClick={() => {
                            setSelectedGrade(grade);
                            openDrawer('student', { student, grade });
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-neutral-900 truncate">{grade.assignment?.title}</h4>
                            {grade.assignment?.due_date && (
                              <p className="text-xs text-neutral-500">Due: {new Date(grade.assignment.due_date).toLocaleDateString()}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-3">
                            {grade.excused ? (
                              <span className="text-sm font-medium text-neutral-500">Excused</span>
                            ) : (
                              <>
                                <span className="text-sm font-semibold text-neutral-900">
                                  {grade.score !== null && grade.score !== undefined ? grade.score : '-'}
                                </span>
                                <span className="text-xs text-neutral-500">/{grade.assignment?.max_score}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                  
                      {/* Show missing assignments */}
                      {missingAssignments.map(assignment => {
                        const missingGrade = {
                          student_id: student.id,
                          assignment_id: assignment.id,
                          score: null,
                          student: student,
                          assignment: assignment
                        };
                        return (
                          <div 
                            key={`missing-${assignment.id}`} 
                            className="flex items-center justify-between px-3 py-2 bg-warning-50 rounded border border-warning-200 cursor-pointer hover:bg-warning-100 transition-colors"
                            onClick={() => {
                              setSelectedGrade(missingGrade);
                              openDrawer('student', { student, grade: missingGrade });
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-neutral-900 truncate">{assignment.title}</h4>
                              {assignment.due_date && (
                                <p className="text-xs text-neutral-500">Due: {new Date(assignment.due_date).toLocaleDateString()}</p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 ml-3">
                              <span className="text-sm font-semibold text-neutral-400">-</span>
                              <span className="text-xs text-neutral-500">/{assignment.max_score}</span>
                            </div>
                          </div>
                        );
                      })}
                </div>
              )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 loading-spinner"></div>
          <p className="text-neutral-600">Loading grades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Grades</h2>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddGradeModal(true)}
            className="btn-primary px-3 py-1.5 text-xs font-medium"
          >
            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Grade
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center gap-1.5 bg-neutral-100 p-0.5 rounded-lg mb-3">
        <button
          onClick={() => setViewMode('assignment')}
          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
            viewMode === 'assignment'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span>By Assignment</span>
          </div>
        </button>
        
        <button
          onClick={() => setViewMode('student')}
          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
            viewMode === 'student'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <span>By Student</span>
          </div>
        </button>
      </div>

      {error && (
        <div className="card p-4 border-l-4 border-error-500 bg-error-50">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-error-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Render the selected view */}
      {viewMode === 'assignment' && renderAssignmentView()}
      {viewMode === 'student' && renderStudentView()}
      {viewMode === 'grid' && renderGridView()}

      {/* Add Grade Modal */}
      {showAddGradeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 safe-top safe-bottom">
          <div className="bg-white rounded-3xl shadow-large w-full max-w-md p-8 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-neutral-900">Add New Grade</h3>
              <button
                onClick={() => setShowAddGradeModal(false)}
                className="p-2 hover:bg-neutral-100 rounded-xl transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleAddGrade} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Student</label>
                <select
                  value={newGrade.student_id}
                  onChange={(e) => setNewGrade({ ...newGrade, student_id: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select Student</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.name}{student.code ? ` · ${student.code}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Assignment</label>
                <select
                  value={newGrade.assignment_id}
                  onChange={(e) => setNewGrade({ ...newGrade, assignment_id: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select Assignment</option>
                  {assignments.map(assignment => (
                    <option key={assignment.id} value={assignment.id}>
                      {assignment.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Score</label>
                <input
                  type="number"
                  value={newGrade.score}
                  onChange={(e) => setNewGrade({ ...newGrade, score: e.target.value })}
                  className="input"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Feedback</label>
                <textarea
                  value={newGrade.feedback}
                  onChange={(e) => setNewGrade({ ...newGrade, feedback: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddGradeModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Add Grade
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Grade Drawer */}
      {drawerOpen && drawerData && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center safe-top safe-bottom"
          onClick={closeDrawer}
        >
          <div 
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-bold text-neutral-900">
                  {drawerType === 'assignment' 
                    ? drawerData.grade?.student?.name || 'Student'
                    : drawerData.grade?.assignment?.title || 'Assignment'
                  }
                </h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {drawerType === 'assignment'
                    ? drawerData.assignment?.title
                    : drawerData.student?.name
                  }
                </p>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {drawerError && (
                <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
                  {drawerError}
                </div>
              )}
              <GradeDrawerForm
                grade={drawerData.grade}
                assignment={drawerType === 'assignment' ? drawerData.assignment : drawerData.grade?.assignment}
                student={drawerType === 'student' ? drawerData.student : drawerData.grade?.student}
                onSave={handleDrawerGradeUpdate}
                onCancel={closeDrawer}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Grade Drawer Form Component
function GradeDrawerForm({ grade, assignment, student, onSave, onCancel }) {
  const isExcused = grade?.excused;
  const [score, setScore] = useState(grade?.score?.toString() ?? '');
  const [feedback, setFeedback] = useState(grade?.feedback || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!score || score === '') return;
    setIsSaving(true);
    await onSave(student?.id || grade?.student_id, assignment?.id || grade?.assignment_id, score, feedback, false);
    setIsSaving(false);
  };

  const handleSetExcused = async (excused) => {
    setIsSaving(true);
    try {
      await onSave(student?.id || grade?.student_id, assignment?.id || grade?.assignment_id, null, feedback, excused);
    } finally {
      setIsSaving(false);
    }
  };

  if (isExcused) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-neutral-100 border border-neutral-200 rounded-lg">
          <p className="text-sm font-medium text-neutral-700">This student is excused from this assignment. It is not included in their grade.</p>
        </div>
        <div className="flex gap-3 pt-4 border-t border-neutral-200">
          <button type="button" onClick={onCancel} className="btn-secondary flex-1" disabled={isSaving}>Cancel</button>
          <button type="button" onClick={() => handleSetExcused(false)} className="btn-primary flex-1" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Remove excuse'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Score</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            className="input flex-1 text-center text-lg font-semibold"
            inputMode="decimal"
            min="0"
            max={assignment?.max_score === 0 ? undefined : (assignment?.max_score ?? 100)}
            step="any"
            placeholder="0"
            required
            autoFocus
          />
          <span className="text-neutral-500">/ {assignment?.max_score === 0 ? 'EC' : (assignment?.max_score ?? 100)}</span>
        </div>
        {score && assignment?.max_score != null && assignment.max_score > 0 && (
          <p className="text-sm text-neutral-500 mt-2">{Math.round((parseFloat(score) / assignment.max_score) * 100)}%</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Feedback (Optional)</label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="input"
          rows={4}
          placeholder="Add feedback for this grade..."
        />
      </div>

      <div className="flex gap-3 pt-4 border-t border-neutral-200">
        <button type="button" onClick={onCancel} className="btn-secondary flex-1" disabled={isSaving}>Cancel</button>
        <button type="submit" className="btn-primary flex-1" disabled={isSaving || !score}>
          {isSaving ? 'Saving...' : grade?.id ? 'Update Grade' : 'Add Grade'}
        </button>
      </div>

      <div className="pt-2 border-t border-neutral-200">
        <button
          type="button"
          onClick={() => handleSetExcused(true)}
          className="text-sm text-neutral-500 hover:text-neutral-700"
          disabled={isSaving}
        >
          Excuse student from this assignment
        </button>
      </div>
    </form>
  );
}
